/**
 * @file apiService.js
 * @description Provides API service objects for authentication and product-related operations.
 * Utilizes a generic apiCall function to interact with backend endpoints.
 *
 * AuthAPI:
 *   - login: Authenticates a user with provided credentials.
 *   - register: Registers a new user with provided data.
 *   - getProfile: Retrieves the authenticated user's profile.
 *
 * ProductAPI:
 *   - fetchAll: Fetches a list of all products.
 *   - fetchById: Fetches details of a product by its ID.
 */
import { apiCall } from "./apiCall";
import { Method } from "./methods";
import { endpoints } from "./endpoints";
import { updateOffers } from "../features/buyer/buyeService";
import { resendOtp } from "../features/auth/authSlice";

export const AuthAPI = {
  login: (credentials) =>
    apiCall({
      method: Method.POST,
      url: endpoints.auth.login,
      data: credentials,
    }),

  register: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.register, data }),

  requestOtp: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.requestOtp, data }),

  verifyOtp: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.verifyOtp, data }),

  verifyLoginOtp: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.verifyLoginOtp, data }),

  resendOtp: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.resendOtp, data }),

  resendVerification: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.resendVerification, data }),

  forgotPassword: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.forotPassword, data }),

  resetPassword: (data) =>
    apiCall({ method: Method.POST, url: endpoints.auth.resetPassword, data }),

  logout: () => apiCall({ method: Method.POST, url: endpoints.auth.logout }),

  getProfile: () =>
    apiCall({ method: Method.GET, url: endpoints.auth.profile }),
};

export const ProductAPI = {
  fetchAll: () => apiCall({ method: Method.GET, url: endpoints.products.list }),

  fetchById: (id) =>
    apiCall({ method: Method.GET, url: endpoints.products.details(id) }),
};

export const UserAPI = {
  viewProfile: () =>
    apiCall({ method: Method.GET, url: endpoints.user.profile.view }),

  updateProfile: (data) =>
    apiCall({ method: Method.POST, url: endpoints.user.profile.update, data }),

  addressList: () =>
    apiCall({ method: Method.GET, url: endpoints.user.profile.addresses }),

  updateAddress: (data) =>
    apiCall({
      method: Method.PUT,
      url: endpoints.user.profile.updateAddress,
      data,
    }),

  changePassword: (data) =>
    apiCall({ method: Method.POST, url: endpoints.user.changePassword, data }),

  kycUpload: (formData) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.kyc.upload,
      data: formData,
    }),

  kycList: () => apiCall({ method: Method.GET, url: endpoints.user.kyc.list }),

  notoficationsList: () =>
    apiCall({ method: Method.GET, url: endpoints.user.notifications.list }),

  notificationsMarkRead: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.notifications.markRead(id),
    }),

  notificationsClearAll: () =>
    apiCall({ method: Method.POST, url: endpoints.user.notifications.clear }),

  tradePrefrenceOptions: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.profile.tradePrefrenceOptions,
    }),
  tradePreferenceStore: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.profile.tradePreferenceStore,
      data,
    }),
  mytradePreference: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.profile.mytradePreference,
    }),

  blogsCategories: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.blogs.blogsCategories,
    }),
  blogsList: () =>
    apiCall({ method: Method.GET, url: endpoints.user.blogs.list }),
  blogsByCategory: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.blogs.blogsByCategory(id),
    }),
  blogDetailBySlug: (slug) =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.blogs.blogDetailBySlug(slug),
    }),
  commonPages: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.blogs.commonPages,
    }),
  pageDetailBySlug: (slug) =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.blogs.pageDetailBySlug(slug),
    }),
  siteMapUrls: () =>
    apiCall({
      methods: Method.GET,
      url: endpoints.user.dynamic.siteMapUrls,
    }),
  footerLinks: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.dynamic.footerLinks,
    }),

  priceExchangeRate: (currency, unit) =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.market.priceExchangeRate(currency, unit),
    }),

  editImage: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.ai.editImage,
      data,
    }),

  charts: (params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.market.charts,
      params,
    }),

  news: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.market.news,
    }),

  chatCreation: (params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chat.chatCreation,
      params,
    }),

  sendMessage: (params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chat.sendMessage,
      params,
    }),

  chatMessages: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.chat.chatMessages(id),
    }),

  chatThreads: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.chat.chatThreads,
    }),

  markAsRead: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chat.markAsRead(id),
    }),

  typing: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chat.typing(id),
    }),

  unreadCount: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.chat.unreadCount,
    }),

  dispute: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chat.dispute(id),
    }),

  //Support Chat
  chatSupportCreation: (params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chatSupport.chatSupportCreation,
      params,
    }),

  sendSupportMessage: (params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chatSupport.sendSupportMessage,
      params,
    }),

  chatSupportMessages: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.chatSupport.chatSupportMessages(id),
    }),

  chatSupportThreads: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.user.chatSupport.chatSupportThreads,
    }),

  markSupportAsRead: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chatSupport.markSupportAsRead(id),
    }),

  closeSupportChat: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.chatSupport.closeSupportChat(id),
    }),
  switchRole: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.userRole.switchRole,
      data,
    }),
  verifyRoleOtp: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.user.userRole.verifyRoleOtp,
      data,
    }),
};

export const BuyerAPI = {
  // --- Products ---
  getAllProducts: () =>
    apiCall({ method: Method.GET, url: endpoints.buyer.products.list }),

  getProductDetail: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.products.detail,
      data,
    }),

  filterProducts: (params) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.products.filter,
      params,
    }),

  getSubcategories: (params) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.products.subcategories,
      params,
    }),

  randomProducts: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.products.randomProducts,
    }),

  // --- Bids ---
  getMyBids: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.bids.list,
    }),
  placeBid: (params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.bids.placeBid,
      params,
    }),
  acceptBid: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.bids.acceptBid(id),
    }),
  rejectBid: (id, params) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.bids.rejectBid(id),
      params,
    }),

  // --- Dashboard ---
  getDashboard: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.dashboard,
    }),

  // --- Orders ---
  myOrders: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.orders.list,
    }),
  orderDetails: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.orders.details(id),
    }),
  orderCancel: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orders.orderCancel(id),
    }),
  orderTrack: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.orders.orderTrack(id),
    }),
  orderPlace: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orders.orderPlace,
      data,
    }),
  orderSummary: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.orders.orderSummary(id),
    }),
  invoice: (data) => {
    return apiCall({
      method: Method.POST,
      url: endpoints.buyer.orders.invoice,
      data,
      config: {
        responseType: "blob", // Required for downloading PDFs
      },
    });
  },

  requestTpi: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orders.requestTpi(id),
    }),

  markComplete: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orders.markComplete(id),
    }),

  paymentMethods: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.payments.methods,
    }),
  checkOutSession: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.payments.createCheckoutSession,
      data,
    }),
  paymentStatus: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.payments.paymentStatus(id),
    }),
  marketData: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.market.data,
    }),

  reverseBids: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.reverseBids.list,
    }),

  storeRequirement: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.reverseBids.store,
      data,
    }),

  requirementDetails: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.reverseBids.details(id),
    }),

  acceptRequirement: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.reverseBids.accept(id),
    }),

  orderShippingDetails: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orderShipping.orderShippingDetails(id),
      data,
    }),

  shippingDetails: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.orderShipping.shippingDetails(id),
    }),

  acceptOrder: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orderShipping.acceptOrder(id),
    }),

  disputeOrder: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.orderShipping.disputeOrder(id),
      data,
    }),

  contactUs: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.contactUs.request,
      data,
    }),

  // --- Offers ---
  getAllOffers: () =>
    apiCall({ method: Method.GET, url: endpoints.buyer.offers.list }),

  getOffersDetail: (id) =>
    apiCall({ method: Method.GET, url: endpoints.buyer.offers.details(id) }),

  addOffer: (data) =>
    apiCall({ method: Method.POST, url: endpoints.buyer.offers.add, data }),

  updateOffers: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.offers.update(id),
      data,
    }),

  deleteOffers: (id) =>
    apiCall({
      method: Method.DELETE,
      url: endpoints.buyer.offers.delete(id),
    }),

  calculatePrice: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.offers.calculatePrice,
      data,
    }),

  getSpecifications: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.offers.specifications(id),
    }),

  getMetalCode: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.offers.metalCode,
      data,
    }),

  respondCounterOffer: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.offers.counterOffer(id),
      data,
    }),

  getPendingTransactions: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.offers.pendingTransactions,
    }),

  getTransactionDetails: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.offers.transactionDetails(id),
    }),

  getCategories: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.offers.categories,
    }),

  getSubcategories: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.offers.subcategories(id),
    }),
  // --- AI ---
  generateDescription: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.ai.generateDescription,
      data,
    }),

  generateFaqs: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.ai.generateFaqs,
      data,
    }),

  //Address Selector
  getCountries: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.addressSelector.countries,
    }),

  getStates: (countryId) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.addressSelector.states,
      params: {
        country_id: countryId,
      },
    }),

  getCities: (stateId) =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.addressSelector.cities,
      params: {
        state_id: stateId,
      },
    }),

  getOutOfRangeList: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.buyer.outOfRange.list,
    }),

  adminAssistance: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.outOfRange.adminAssistance(id),
      data,
    }),

  acceptCharges: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.outOfRange.acceptCharges(id),
    }),

  rejectCharges: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.buyer.outOfRange.rejectCharges(id),
    }),
};

export const SellerAPI = {
  // --- Products ---
  getAllProducts: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.products.list }),

  getProductDetail: (id) =>
    apiCall({ method: Method.GET, url: endpoints.seller.products.details(id) }),

  addProduct: (data) =>
    apiCall({ method: Method.POST, url: endpoints.seller.products.add, data }),

  updateProduct: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.products.update(id),
      data,
    }),

  deleteProduct: (id, data) =>
    apiCall({
      method: Method.DELETE,
      url: endpoints.seller.products.delete(id),
      data,
    }),

  trashProducts: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.products.trash }),

  restoreProducts: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.products.restore(id),
      data,
    }),

  calculatePrice: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.products.calculatePrice,
      data,
    }),

  getDashboard: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.products.dashboard }),

  getStockList: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.products.stockList }),

  updateStock: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.products.updateStock(id),
      data,
    }),

  getSpecifications: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.products.specifications(id),
    }),

  metalPrice: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.products.metalPrice,
      data,
    }),

  getMetalCode: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.products.metalCode,
      data,
    }),

  // --- Bids ---
  getBids: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.bids.list }),

  updateBidStatus: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.bids.statusUpdate(id),
      data,
    }),

  //--- Orders ---
  getOrders: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.orders.list }),

  getOrderDetails: (id) =>
    apiCall({ method: Method.GET, url: endpoints.seller.orders.details(id) }),

  updateOrderStatus: (id, data) =>
    apiCall({
      method: Method.PUT,
      url: endpoints.seller.orders.orderStatusUpdate(id),
      data,
    }),

  getShippingPartners: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.orders.shippingPartnerList,
    }),

  updateShippingPartner: (id, data) =>
    apiCall({
      method: Method.PUT,
      url: endpoints.seller.orders.shippinhPartnerUpdate(id),
      data,
    }),

  updateTrackingNumber: (data) =>
    apiCall({
      method: Method.PUT,
      url: endpoints.seller.orders.updateTrackingNumber,
      data,
    }),

  getInvoiceList: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.orders.invoiceList }),

  getInvoiceDetails: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.orders.invoiceDetails(id),
    }),

  // --- Categories ---
  getCategories: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.categories.list }),

  // --- Subcategories ---
  getSubcategories: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.subcategories.list(id),
    }),

  // --- Reverse Bids ---
  getReverseBids: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.reverseBids.list }),
  getReverseBidOrders: () =>
    apiCall({ method: Method.GET, url: endpoints.seller.reverseBids.orders }),
  storeReverseBid: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.reverseBids.store(id),
      data,
    }),

  // --- Certificates ---
  uploadCertificate: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.certificates.upload,
      data,
    }),
  getCertificates: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.certificates.list,
    }),

  // --- Payments ---
  getPaymentMethods: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.payments.methods,
    }),

  getAccountStatus: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.payments.accountStatus,
    }),

  // --- AI ---
  generateDescription: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.ai.generateDescription,
      data,
    }),

  generateFaqs: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.ai.generateFaqs,
      data,
    }),

  generateVideoUrl: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.products.generatVideoUrl(id),
    }),

  // --- Offers ---
  getReceivedOffers: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.offers.receivedOffers,
    }),
  respondCounterOffer: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.offers.respondCounterOffer(id),
      data,
    }),

  receivedOffersDetail: (id) =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.offers.detail(id),
    }),

  getOutOfRangeList: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.outOfRange.list,
    }),

  approveRequest: (id, data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.outOfRange.approveRequest(id),
      data,
    }),

  rejectRequest: (id) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.outOfRange.rejectRequest(id),
    }),

  onboardingList: () =>
    apiCall({
      method: Method.GET,
      url: endpoints.seller.onboarding.list,
    }),
    
  onboardingSubmit: (data) =>
    apiCall({
      method: Method.POST,
      url: endpoints.seller.onboarding.submit,
      data,
    }),
};
